import { useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { CornerDownLeft, LogOut, Plus, UserRound } from "lucide-react";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@/components/ui/dialog";
import { useAuth } from "@/contexts/AuthContext";
import { ADMIN_ROUTES, NAV_GROUPS, canOpen } from "@/navigation/registry";
import { ROLES_CONTENT, hasAnyRole } from "@/lib/roles";

type CommandPaletteProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
};

/**
 * ⌘K / Ctrl+K: jump to any page this person can open.
 * Pages come from the route registry, already filtered by role.
 */
const CommandPalette = ({ open, onOpenChange }: CommandPaletteProps) => {
  const navigate = useNavigate();
  const { user, signOut } = useAuth();
  const roleIds = useMemo(() => user?.roleIds ?? [], [user?.roleIds]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        onOpenChange(!open);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onOpenChange]);

  const sections = useMemo(
    () =>
      NAV_GROUPS.map((g) => ({
        group: g,
        items: ADMIN_ROUTES.filter(
          (r) => (r.nav?.group === g.id || (r.searchable && groupOf(r.path, r.parent) === g.id)) && canOpen(r, roleIds)
        ),
      })).filter((s) => s.items.length),
    [roleIds]
  );

  const go = (path: string) => {
    onOpenChange(false);
    navigate(path);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="overflow-hidden p-0 max-w-[560px] gap-0 top-[20%] translate-y-0">
        <DialogTitle className="sr-only">Search or jump to</DialogTitle>
        <DialogDescription className="sr-only">
          Type a page name, then press Enter to open it.
        </DialogDescription>
        <Command
          loop
          className="[&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:text-xs [&_[cmdk-group-heading]]:font-semibold [&_[cmdk-group-heading]]:uppercase [&_[cmdk-group-heading]]:tracking-[0.8px] [&_[cmdk-group-heading]]:text-ink-3 [&_[cmdk-item]]:h-10 [&_[cmdk-item]]:rounded-lg"
        >
          <CommandInput placeholder="Search pages: books, Mizo strings, plans…" className="h-12 text-[15px]" />
          <CommandList className="max-h-[420px]">
            <CommandEmpty>No page matches. Try a shorter word.</CommandEmpty>
            {hasAnyRole(user, ROLES_CONTENT) && (
              <CommandGroup heading="Actions">
                <CommandItem value="add new book create" onSelect={() => go("/admin/books/new")}>
                  <Plus className="mr-2 h-4 w-4" aria-hidden="true" />
                  Add a book
                </CommandItem>
              </CommandGroup>
            )}
            {sections.map(({ group, items }) => (
              <CommandGroup key={group.id} heading={group.label}>
                {items.map((r) => (
                  <CommandItem
                    key={r.path}
                    value={`${r.title} ${group.label} ${r.nav?.section ?? ""} ${r.keywords ?? ""} ${r.path}`}
                    onSelect={() => go(r.path)}
                  >
                    <group.icon className="mr-2 h-4 w-4 text-ink-3" aria-hidden="true" />
                    <span className="flex-1">{r.title}</span>
                    {r.nav?.section && <span className="text-xs text-ink-3">{r.nav.section}</span>}
                  </CommandItem>
                ))}
              </CommandGroup>
            ))}
            <CommandGroup heading="Account">
              <CommandItem value="your profile account" onSelect={() => go("/admin/profile")}>
                <UserRound className="mr-2 h-4 w-4" aria-hidden="true" />
                Your profile
              </CommandItem>
              <CommandItem
                value="sign out log out"
                onSelect={async () => {
                  onOpenChange(false);
                  await signOut();
                  navigate("/admin/login", { replace: true });
                }}
              >
                <LogOut className="mr-2 h-4 w-4" aria-hidden="true" />
                Sign out
              </CommandItem>
            </CommandGroup>
          </CommandList>
          <div className="flex items-center gap-3 border-t px-3 py-2 text-xs text-ink-3">
            <span className="inline-flex items-center gap-1">
              <CornerDownLeft className="h-3.5 w-3.5" aria-hidden="true" /> open
            </span>
            <span>↑ ↓ move</span>
            <span>Esc close</span>
          </div>
        </Command>
      </DialogContent>
    </Dialog>
  );
};

const groupOf = (path: string, parent?: string) => {
  const target = ADMIN_ROUTES.find((r) => r.path === (parent ?? path));
  return target?.nav?.group ?? "today";
};

export default CommandPalette;
